import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Alert,
  AlertIcon,
  Badge,
  Card,
  CardBody,
  Checkbox,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  Textarea,
  VStack,
  HStack,
  FormErrorMessage,
  useToast,
  Spinner,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  Divider,
  Stack,
  SimpleGrid,
  Tooltip,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionIcon,
  AccordionPanel,
  Flex,
} from "@chakra-ui/react";
import ReactSelect from "react-select";
import { BsPlus, BsTrash, BsPaperclip, BsXCircle } from "react-icons/bs";
import vendorApi from "../services/vendorApi";
import vendorPOApi from "../services/vendorPOApi";

const createEmptyLineItem = () => ({
  id: Date.now() + Math.random(),
  requestLineItemId: "",
  itemName: "",
  description: "",
  quantity: 1,
  unit: "pcs",
  unitPrice: "",
  discountAmount: 0,
  taxRate: 0,
  totalPrice: 0,
  taxAmount: 0,
  netAmount: 0,
  notes: "",
});

const numericToFixed = (value) =>
  Number.parseFloat(Number(value || 0).toFixed(2));

const sanitizeNumericInput = (value) =>
  String(value || "").replace(/[^0-9.]/g, "");

const getDraftStorageKey = (requestId) =>
  `vendor-po-draft-${requestId || "new"}`;

const calculateFinancialSummary = (lineItems, formData) => {
  let subtotal = 0;

  lineItems.forEach((item) => {
    const quantity = Number(item.quantity || 0);
    const unitPrice = Number(item.unitPrice || 0);
    const discountAmount = Number(item.discountAmount || 0);

    const lineTotal = quantity * unitPrice;
    const taxableValue = lineTotal - discountAmount;

    subtotal += taxableValue;
  });

  const shippingCharges = Number(formData.shippingCharges || 0);
  const packingCharges = Number(formData.packingCharges || 0);
  const otherCharges = Number(formData.otherCharges || 0);
  const headerDiscount = Number(formData.discountAmount || 0);

  const taxableAmount =
    subtotal - headerDiscount + shippingCharges + packingCharges + otherCharges;

  const cgstRate = Number(formData.cgstRate || 0);
  const sgstRate = Number(formData.sgstRate || 0);
  const igstRate = Number(formData.igstRate || 0);

  const cgstAmount = (taxableAmount * cgstRate) / 100;
  const sgstAmount = (taxableAmount * sgstRate) / 100;
  const igstAmount = (taxableAmount * igstRate) / 100;
  const totalTaxAmount = cgstAmount + sgstAmount + igstAmount;
  const roundOffAmount = Number(formData.roundOffAmount || 0);
  const grandTotal = taxableAmount + totalTaxAmount + roundOffAmount;

  return {
    subtotal: Number(subtotal.toFixed(2)),
    taxableAmount: Number(taxableAmount.toFixed(2)),
    cgstAmount: Number(cgstAmount.toFixed(2)),
    sgstAmount: Number(sgstAmount.toFixed(2)),
    igstAmount: Number(igstAmount.toFixed(2)),
    totalTaxAmount: Number(totalTaxAmount.toFixed(2)),
    grandTotal: Number(grandTotal.toFixed(2)),
  };
};

const normalizeAmount = (value) => {
  if (value && typeof value === "object" && value.$numberDecimal) {
    return Number(value.$numberDecimal);
  }

  return Number(value || 0);
};

const formatFileSize = (bytes) => {
  const sizeInBytes = Number(bytes || 0);
  if (sizeInBytes <= 0) {
    return "0 KB";
  }

  return `${(sizeInBytes / 1024).toFixed(1)} KB`;
};

const randomInt = (min, max) =>
  Math.floor(Math.random() * (max - min + 1)) + min;

const randomCode = () =>
  `${Date.now().toString().slice(-6)}${randomInt(100, 999)}`;

const AddVendorPOModal = ({
  isOpen,
  onClose,
  requestId,
  onSuccess,
  requestLineItems = [],
  proposalStatus = "",
  requestStatus = "",
  renderMode = "modal",
}) => {
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [vendorsLoading, setVendorsLoading] = useState(false);
  console.log({ vendorsLoading });
  const [vendors, setVendors] = useState([]);
  const [formData, setFormData] = useState({
    vendorId: "",
    poNumber: "",
    currency: "INR",
    poDocumentUrl: "",
    notes: "",
    discountAmount: 0,
    shippingCharges: 0,
    packingCharges: 0,
    otherCharges: 0,
    roundOffAmount: 0,
    cgstRate: 0,
    sgstRate: 0,
    igstRate: 0,
    gstin: "",
    hsnSacCode: "",
    placeOfSupply: "",
    baseCurrency: "INR",
    exchangeRate: 1,
    taxType: "GST",
    reverseChargeApplicable: false,
    termsAndConditions: "",
    paymentTerms: "",
    deliveryTimeline: "",
    warrantyTerms: "",
    quotationValidUntil: "",
    specialNotes: "",
    expectedDeliveryDate: "",
    leadTime: "",
    subtotal: 0,
    taxableAmount: 0,
    cgstAmount: 0,
    sgstAmount: 0,
    igstAmount: 0,
    totalTaxAmount: 0,
    grandTotal: 0,
  });
  const [proposalLineItems, setProposalLineItems] = useState([]);
  const [errors, setErrors] = useState({});
  const [poDocumentFile, setPoDocumentFile] = useState(null);
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const poDocumentInputRef = useRef(null);
  const isPageMode = renderMode === "page";
  const isCreationLocked = ["REJECTED", "SENT_BACK"].includes(requestStatus);
  const isReadOnlyProposal = proposalStatus === "APPROVED" || isCreationLocked;
  const Shell = isPageMode ? Box : Modal;
  const ShellContent = isPageMode ? Box : ModalContent;
  const ShellBody = isPageMode ? Box : ModalBody;
  const ShellFooter = isPageMode ? Box : ModalFooter;
  const shellProps = isPageMode
    ? {
        minH: "100vh",
        bg: "linear-gradient(180deg, #f6f8fc 0%, #eef3fb 100%)",
        px: { base: 4, md: 6, xl: 8 },
        py: { base: 4, md: 6 },
      }
    : { isOpen, onClose, size: "6xl" };

  console.log({ requestLineItems });

  const surfaceCardProps = {
    variant: "outline",
    borderRadius: "2xl",
    boxShadow: "sm",
    borderColor: "gray.200",
    bg: "white",
  };

  const softPanelProps = {
    borderWidth: "1px",
    borderColor: "gray.200",
    borderRadius: "xl",
    bg: "gray.50",
  };

  const formatCurrency = (value) =>
    new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: formData.currency || "INR",
      minimumFractionDigits: 2,
    }).format(Number(value || 0));

  const effectiveTaxRate =
    Number(formData.cgstRate || 0) +
    Number(formData.sgstRate || 0) +
    Number(formData.igstRate || 0);

  const lineItemHasError = (item) => {
    const quantity = Number(item.quantity || 0);
    const unitPrice = Number(item.unitPrice || 0);
    const discountAmount = Number(item.discountAmount || 0);
    return (
      !item.itemName?.trim() ||
      quantity <= 0 ||
      unitPrice < 0 ||
      discountAmount < 0 ||
      Number(item.taxRate || 0) < 0 ||
      Number(item.netAmount || 0) < 0
    );
  };

  const vendorOptions = vendors.map((vendor) => ({
    value: vendor._id,
    label: `${vendor.name} (${vendor.email})`,
  }));

  const initializeProposalLineItems = () => [createEmptyLineItem()];

  const readDraft = () => {
    if (!requestId || typeof window === "undefined") {
      return null;
    }

    try {
      const draft = window.localStorage.getItem(getDraftStorageKey(requestId));
      return draft ? JSON.parse(draft) : null;
    } catch (error) {
      console.error("Failed to read vendor PO draft:", error);
      return null;
    }
  };

  const writeDraft = (draft) => {
    if (!requestId || typeof window === "undefined") {
      return;
    }

    try {
      window.localStorage.setItem(
        getDraftStorageKey(requestId),
        JSON.stringify(draft),
      );
    } catch (error) {
      console.error("Failed to save vendor PO draft:", error);
    }
  };

  const clearDraft = () => {
    if (!requestId || typeof window === "undefined") {
      return;
    }

    try {
      window.localStorage.removeItem(getDraftStorageKey(requestId));
    } catch (error) {
      console.error("Failed to clear vendor PO draft:", error);
    }
  };

  useEffect(() => {
    fetchActiveVendors();

    if (isOpen) {
      fetchActiveVendors();
      // Reset form when modal opens
      const draft = readDraft();
      setFormData({
        vendorId: "",
        poNumber: "",
        currency: "INR",
        poDocumentUrl: "",
        notes: "",
        discountAmount: 0,
        shippingCharges: 0,
        packingCharges: 0,
        otherCharges: 0,
        roundOffAmount: 0,
        cgstRate: 0,
        sgstRate: 0,
        igstRate: 0,
        gstin: "",
        hsnSacCode: "",
        placeOfSupply: "",
        baseCurrency: "INR",
        exchangeRate: 1,
        taxType: "GST",
        reverseChargeApplicable: false,
        termsAndConditions: "",
        paymentTerms: "",
        deliveryTimeline: "",
        warrantyTerms: "",
        quotationValidUntil: "",
        specialNotes: "",
        expectedDeliveryDate: "",
        leadTime: "",
        subtotal: 0,
        taxableAmount: 0,
        cgstAmount: 0,
        sgstAmount: 0,
        igstAmount: 0,
        totalTaxAmount: 0,
        grandTotal: 0,
        ...(draft?.formData || {}),
      });
      setProposalLineItems(
        draft?.proposalLineItems?.length
          ? draft.proposalLineItems
          : initializeProposalLineItems(),
      );
      setErrors({});
      setPoDocumentFile(null);
      if (poDocumentInputRef.current) {
        poDocumentInputRef.current.value = "";
      }
    }
  }, [isOpen, requestId]);

  useEffect(() => {
    const summary = calculateFinancialSummary(proposalLineItems, formData);
    setFormData((prev) => ({
      ...prev,
      ...summary,
    }));
  }, [
    proposalLineItems,
    formData.discountAmount,
    formData.shippingCharges,
    formData.packingCharges,
    formData.otherCharges,
    formData.cgstRate,
    formData.sgstRate,
    formData.igstRate,
    formData.roundOffAmount,
  ]);

  useEffect(() => {
    const syncTaxRate = () => {
      setProposalLineItems((items) =>
        items.map((item) => {
          const quantity = Number(item.quantity || 0);
          const unitPrice = Number(item.unitPrice || 0);
          const discountAmount = Number(item.discountAmount || 0);
          const totalPrice = quantity * unitPrice;
          const taxableValue = totalPrice - discountAmount;
          const taxAmount = (taxableValue * effectiveTaxRate) / 100;

          return {
            ...item,
            taxRate: effectiveTaxRate,
            totalPrice: numericToFixed(totalPrice),
            taxAmount: numericToFixed(taxAmount),
            netAmount: numericToFixed(taxableValue + taxAmount),
          };
        }),
      );
    };

    syncTaxRate();
  }, [formData.cgstRate, formData.sgstRate, formData.igstRate]);

  useEffect(() => {
    if (!isOpen || isReadOnlyProposal) {
      return undefined;
    }

    const autosave = () => {
      writeDraft({
        formData,
        proposalLineItems,
      });
    };

    autosave();
    const intervalId = window.setInterval(autosave, 15000);
    return () => window.clearInterval(intervalId);
  }, [isOpen, isReadOnlyProposal, formData, proposalLineItems]);

  const fetchActiveVendors = async () => {
    try {
      setVendorsLoading(true);
      const response = await vendorApi.getActiveVendors();
      console.log("Vendor API Response:", response);

      setVendors(
        response?.data?.data || response?.data?.vendors || response?.data || [],
      );
    } catch (error) {
      console.error("❌ Error fetching vendors:", error);
      toast({
        title: "Error fetching vendors",
        description: error.response?.data?.message || error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setVendorsLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors({ ...errors, [field]: null });
    }
  };

  const handleNumberInputChange = (field, value) => {
    const parsed = value === "" ? 0 : Number(sanitizeNumericInput(value));
    handleInputChange(field, Number.isFinite(parsed) ? parsed : 0);
  };

  const handleTextNumberChange = (field, value) => {
    handleInputChange(field, sanitizeNumericInput(value));
  };

  const updateLineItem = (id, field, value) => {
    setProposalLineItems((currentItems) =>
      currentItems.map((item) => {
        if (item.id !== id) {
          return item;
        }

        const updatedItem = {
          ...item,
          [field]:
            field === "quantity" ||
            field === "unitPrice" ||
            field === "discountAmount"
              ? sanitizeNumericInput(value)
              : value,
        };
        const quantity = Number(updatedItem.quantity || 0);
        const unitPrice = Number(updatedItem.unitPrice || 0);
        const discountAmount = Number(updatedItem.discountAmount || 0);
        const taxRate = effectiveTaxRate;
        const totalPrice = quantity * unitPrice;
        const taxableValue = totalPrice - discountAmount;
        const taxAmount = (taxableValue * taxRate) / 100;
        const netAmount = taxableValue + taxAmount;

        updatedItem.totalPrice = Number(totalPrice.toFixed(2));
        updatedItem.taxAmount = Number(taxAmount.toFixed(2));
        updatedItem.netAmount = Number(netAmount.toFixed(2));
        return updatedItem;
      }),
    );

    if (errors.lineItems) {
      setErrors({ ...errors, lineItems: null });
    }
  };

  const addLineItem = () => {
    setProposalLineItems((currentItems) => [
      ...currentItems,
      createEmptyLineItem(),
    ]);
  };

  const removeLineItem = (id) => {
    setProposalLineItems((currentItems) => {
      if (currentItems.length === 1) {
        return currentItems;
      }
      return currentItems.filter((item) => item.id !== id);
    });
  };

  useEffect(() => {
    if (!requestLineItems?.length) return;

    const mappedItems = requestLineItems.map((item, index) => {
      const quantity = Number(item.quantity || 0);
      const unitPrice = Number(item.unitPrice || 0);
      const discountAmount = 0;

      const totalPrice = quantity * unitPrice;
      const taxableValue = totalPrice - discountAmount;
      const taxAmount = (taxableValue * effectiveTaxRate) / 100;
      const netAmount = taxableValue + taxAmount;

      return {
        id: item._id || `${Date.now()}-${index}`,
        requestLineItemId: item._id || "",

        itemName: item.itemName || "",
        description: item.description || "",

        quantity,
        unit: item.unit || "pcs",
        unitPrice,

        discountAmount,
        taxRate: effectiveTaxRate,

        totalPrice: Number(totalPrice.toFixed(2)),
        taxAmount: Number(taxAmount.toFixed(2)),
        netAmount: Number(netAmount.toFixed(2)),

        notes: "",
      };
    });

    setProposalLineItems(mappedItems);
  }, [requestLineItems, effectiveTaxRate]);

  const calculateQuotedAmount = () =>
    calculateFinancialSummary(proposalLineItems, formData).grandTotal;

  const handleQuickFillTestData = () => {
    const randomVendor = vendors[randomInt(0, Math.max(vendors.length - 1, 0))];

    setFormData((currentData) => ({
      ...currentData,

      vendorId: currentData.vendorId || randomVendor?._id || "",

      poNumber:
        currentData.poNumber ||
        `QT-${new Date().getFullYear()}-${randomCode()}`,

      currency: currentData.currency || "INR",

      notes:
        currentData.notes ||
        `Test quotation generated on ${new Date().toLocaleString()}`,

      // Commercial Charges
      discountAmount: Number(currentData.discountAmount) || randomInt(0, 5000),

      shippingCharges:
        Number(currentData.shippingCharges) || randomInt(500, 2500),

      packingCharges:
        Number(currentData.packingCharges) || randomInt(100, 1000),

      otherCharges: Number(currentData.otherCharges) || randomInt(0, 1500),

      roundOffAmount: Number(currentData.roundOffAmount) || 0,

      // GST
      cgstRate: Number(currentData.cgstRate) || 9,

      sgstRate: Number(currentData.sgstRate) || 9,

      igstRate: Number(currentData.igstRate) || 0,

      gstin: currentData.gstin || "27ABCDE1234F1Z5",

      hsnSacCode: currentData.hsnSacCode || "8471",

      placeOfSupply: currentData.placeOfSupply || "MAHARASHTRA",

      // Terms
      paymentTerms:
        currentData.paymentTerms || "100% within 30 days from invoice date",

      deliveryTimeline: currentData.deliveryTimeline || "7-10 business days",

      warrantyTerms: currentData.warrantyTerms || "1 year standard warranty",

      termsAndConditions:
        currentData.termsAndConditions ||
        "Prices are exclusive of applicable taxes unless specified otherwise.",

      specialNotes:
        currentData.specialNotes || "Material subject to stock availability.",

      quotationValidUntil:
        currentData.quotationValidUntil ||
        new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
          .toISOString()
          .split("T")[0],

      expectedDeliveryDate:
        currentData.expectedDeliveryDate ||
        new Date(Date.now() + 14 * 24 * 60 * 60 * 1000)
          .toISOString()
          .split("T")[0],

      leadTime: currentData.leadTime || "14",
    }));

    setProposalLineItems(() => {
      // USE REAL REQUEST ITEMS IF AVAILABLE
      if (requestLineItems?.length > 0) {
        return requestLineItems.map((lineItem, index) => {
          const quantity = Number(lineItem.quantity || 1);

          // preserve backend value if present
          const unitPrice =
            Number(lineItem.unitPrice || 0) || randomInt(1000, 25000);

          const discountAmount = 0;

          const totalPrice = quantity * unitPrice;

          const taxRate =
            Number(formData.cgstRate || 0) +
            Number(formData.sgstRate || 0) +
            Number(formData.igstRate || 0);

          const taxAmount = ((totalPrice - discountAmount) * taxRate) / 100;

          return {
            id: lineItem._id || `${Date.now()}-${index}`,

            requestLineItemId: lineItem._id || "",

            itemName: lineItem.itemName || "",

            description:
              lineItem.description ||
              `Supply of ${lineItem.itemName || "material"}`,

            quantity,

            unit: lineItem.unit || "pcs",

            unitPrice,

            discountAmount,

            taxRate,

            totalPrice: numericToFixed(totalPrice),

            taxAmount: numericToFixed(taxAmount),

            netAmount: numericToFixed(totalPrice - discountAmount + taxAmount),

            notes: "",
          };
        });
      }

      // FALLBACK TEST DATA
      return [
        {
          ...createEmptyLineItem(),
          itemName: "APC Smart UPS 3000VA",
          description: "Industrial UPS supply",
          quantity: 2,
          unit: "pcs",
          unitPrice: 25000,
        },
        {
          ...createEmptyLineItem(),
          itemName: "Installation Service",
          description: "Onsite installation and commissioning",
          quantity: 1,
          unit: "job",
          unitPrice: 5000,
        },
      ].map((item) => {
        const totalPrice = Number(item.quantity) * Number(item.unitPrice);

        const taxRate =
          Number(formData.cgstRate || 0) +
          Number(formData.sgstRate || 0) +
          Number(formData.igstRate || 0);

        const taxAmount = (totalPrice * taxRate) / 100;

        return {
          ...item,
          taxRate,
          totalPrice: numericToFixed(totalPrice),
          taxAmount: numericToFixed(taxAmount),
          netAmount: numericToFixed(totalPrice + taxAmount),
        };
      });
    });

    setErrors({});

    toast({
      title: "Test data filled",
      description: "Quotation test dataset applied",
      status: "success",
      duration: 1500,
      isClosable: true,
    });
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.vendorId) {
      newErrors.vendorId = "Please select a vendor";
    }

    if (!formData.poNumber || formData.poNumber.trim().length < 3) {
      newErrors.poNumber = "Quotation Number must be at least 3 characters";
    }

    if (proposalLineItems.length === 0) {
      newErrors.lineItems = "At least one line item is required";
    }

    const invalidLineItems = proposalLineItems.filter(
      (item) =>
        !item.itemName?.trim() ||
        !item.quantity ||
        Number(item.quantity) <= 0 ||
        item.unitPrice === "" ||
        Number(item.unitPrice) < 0 ||
        Number(item.discountAmount || 0) < 0 ||
        Number(item.taxRate || 0) < 0,
    );
    if (invalidLineItems.length > 0) {
      newErrors.lineItems =
        "Each line item must have item name, quantity, and unit price";
    }

    if (calculateQuotedAmount() <= 0) {
      newErrors.lineItems = "Grand total must be greater than 0";
    }

    const hasIGST = Number(formData.igstRate || 0) > 0;
    const hasCGSTSGST =
      Number(formData.cgstRate || 0) > 0 || Number(formData.sgstRate || 0) > 0;

    if (hasIGST && hasCGSTSGST) {
      newErrors.tax = "Cannot apply IGST together with CGST/SGST";
    }

    const gstRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[A-Z0-9]{3}$/;
    if (formData.gstin && !gstRegex.test(formData.gstin)) {
      newErrors.gstin = "Invalid GSTIN format";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!requestId) {
      toast({
        title: "Save draft first",
        description:
          "Please save this procurement request as Draft before adding vendor quotations or uploading quotation files.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);

      const summary = calculateFinancialSummary(proposalLineItems, formData);

      const proposalData = {
        ...formData,
        ...summary,
        poDocumentAttachmentUrl: "",
        poDocumentAttachmentName: "",
        lineItems: proposalLineItems.map(({ id, ...item }) => ({
          ...item,
          quantity: Number(item.quantity || 0),
          unitPrice: Number(item.unitPrice || 0),
          totalPrice: Number(item.totalPrice || 0),
          discountAmount: Number(item.discountAmount || 0),
          taxRate: Number(item.taxRate || 0),
          taxAmount: Number(item.taxAmount || 0),
          netAmount: Number(item.netAmount || 0),
        })),
      };

      if (poDocumentFile) {
        setUploadingDocument(true);
        const uploadedDocResponse = await vendorPOApi.uploadVendorPODocument(
          requestId,
          poDocumentFile,
        );
        const uploadedDoc = uploadedDocResponse?.data;
        if (uploadedDoc?.url) {
          proposalData.poDocumentAttachmentUrl = uploadedDoc.url;
          proposalData.poDocumentAttachmentName =
            uploadedDoc.filename || poDocumentFile.name;
        }
      }

      await vendorPOApi.addVendorPOProposal(requestId, proposalData);
      clearDraft();

      toast({
        title: "Vendor PO Proposal added successfully",
        status: "success",
        duration: 2000,
        isClosable: true,
      });

      await Promise.resolve(onSuccess?.());
      onClose?.();
    } catch (error) {
      console.error("❌ Error adding vendor PO proposal:", error);
      toast({
        title: "Error adding proposal",
        description: error.response?.data?.message || error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
      setUploadingDocument(false);
    }
  };

  return (
    <Shell {...shellProps}>
      {!isPageMode ? <ModalOverlay /> : null}
      <ShellContent
        maxW={isPageMode ? "100%" : { base: "full", md: "95%", lg: "1100px" }}
        mx={isPageMode ? "auto" : undefined}
        borderRadius={isPageMode ? "2xl" : undefined}
        overflow={isPageMode ? "hidden" : undefined}
        bg={isPageMode ? "white" : undefined}
        boxShadow={isPageMode ? "xl" : undefined}
      >
        {isPageMode ? (
          <Box px={{ base: 4, md: 6 }} pt={{ base: 5, md: 6 }} pb={4}>
            <HStack justify="space-between" align="start" spacing={4}>
              <Box>
                <Text fontSize={{ base: "xl", md: "2xl" }} fontWeight="bold">
                  {isReadOnlyProposal
                    ? "Approved Proposal"
                    : "Add Vendor PO Proposal"}
                </Text>
                <Text fontSize="sm" color="gray.500" mt={1}>
                  Create a GST-compliant vendor quotation with live financial
                  calculations.
                </Text>
              </Box>
              <Button variant="ghost" onClick={onClose}>
                Back
              </Button>
            </HStack>
          </Box>
        ) : (
          <ModalHeader>
            {isReadOnlyProposal
              ? "Approved Proposal"
              : "Add Vendor PO Proposal"}
          </ModalHeader>
        )}
        {!isPageMode ? <ModalCloseButton /> : null}
        <form onSubmit={handleSubmit}>
          <ShellBody
            px={isPageMode ? { base: 4, md: 6 } : undefined}
            py={isPageMode ? 4 : undefined}
          >
            <VStack spacing={4} align="stretch">
              {isReadOnlyProposal ? (
                <Alert status="info" borderRadius="md">
                  <AlertIcon />
                  Approved Proposal — Financials Locked
                </Alert>
              ) : null}

              <HStack justify="flex-end">
                <Button
                  size="xs"
                  variant="outline"
                  onClick={handleQuickFillTestData}
                  isDisabled={vendorsLoading || isReadOnlyProposal}
                >
                  Quick Fill Test Data
                </Button>
              </HStack>

              {/* Vendor Selection */}
              <FormControl isInvalid={errors.vendorId} isRequired>
                <FormLabel>Select Vendor</FormLabel>
                {vendorsLoading ? (
                  <Spinner size="sm" />
                ) : vendors.length === 0 ? (
                  <Text fontSize="sm" color="gray.500">
                    No active vendors available. Please add vendors first.
                  </Text>
                ) : (
                  <ReactSelect
                    placeholder="Search and select a vendor"
                    isClearable
                    options={vendorOptions}
                    value={
                      vendorOptions.find(
                        (option) => option.value === formData.vendorId,
                      ) || null
                    }
                    isDisabled={isReadOnlyProposal}
                    onChange={(selectedOption) =>
                      handleInputChange(
                        "vendorId",
                        selectedOption ? selectedOption.value : "",
                      )
                    }
                  />
                )}
                <FormErrorMessage>{errors.vendorId}</FormErrorMessage>
              </FormControl>

              {/* Quotation Number */}
              <FormControl isInvalid={errors.poNumber} isRequired>
                <FormLabel>Quotation Number</FormLabel>
                <Input
                  placeholder="e.g., QT-2026-001"
                  value={formData.poNumber}
                  isReadOnly={isReadOnlyProposal}
                  onChange={(e) =>
                    handleInputChange("poNumber", e.target.value)
                  }
                />
                <FormErrorMessage>{errors.poNumber}</FormErrorMessage>
              </FormControl>

              <FormControl>
                <FormLabel>Currency</FormLabel>
                <Select
                  value={formData.currency}
                  isDisabled={isReadOnlyProposal}
                  onChange={(e) =>
                    handleInputChange("currency", e.target.value)
                  }
                >
                  <option value="INR">₹ INR</option>
                  <option value="USD">$ USD</option>
                  <option value="EUR">€ EUR</option>
                  <option value="GBP">£ GBP</option>
                </Select>
              </FormControl>

              <SimpleGrid
                columns={{ base: 1, xl: 1 }}
                spacing={6}
                alignItems="start"
              >
                <Box>
                  <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
                    <Card variant="outline" borderRadius="xl" boxShadow="sm">
                      <CardBody>
                        <Text fontWeight="semibold" fontSize="lg" mb={4}>
                          Commercial Charges
                        </Text>
                        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                          {[
                            ["discountAmount", "Header Discount"],
                            ["shippingCharges", "Shipping Charges"],
                            ["packingCharges", "Packing Charges"],
                            ["otherCharges", "Other Charges"],
                            ["roundOffAmount", "Round Off"],
                          ].map(([field, label]) => (
                            <FormControl key={field}>
                              <FormLabel>{label}</FormLabel>
                              <Input
                                type="text"
                                inputMode="decimal"
                                value={formData[field]}
                                isDisabled={isReadOnlyProposal}
                                onChange={(e) =>
                                  handleTextNumberChange(field, e.target.value)
                                }
                              />
                            </FormControl>
                          ))}
                        </SimpleGrid>
                      </CardBody>
                    </Card>

                    <Card variant="outline" borderRadius="xl" boxShadow="sm">
                      <CardBody>
                        <Text fontWeight="semibold" fontSize="lg" mb={4}>
                          GST Configuration
                        </Text>
                        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                          {[
                            ["cgstRate", "CGST %"],
                            ["sgstRate", "SGST %"],
                            ["igstRate", "IGST %"],
                          ].map(([field, label]) => (
                            <FormControl key={field} isInvalid={errors.tax}>
                              <FormLabel>{label}</FormLabel>
                              <Input
                                type="text"
                                inputMode="decimal"
                                value={formData[field]}
                                isDisabled={isReadOnlyProposal}
                                onChange={(e) =>
                                  handleTextNumberChange(field, e.target.value)
                                }
                              />
                            </FormControl>
                          ))}

                          <FormControl>
                            <FormLabel>GSTIN</FormLabel>
                            <Input
                              placeholder="27ABCDE1234F1Z5"
                              value={formData.gstin}
                              isDisabled={isReadOnlyProposal}
                              onChange={(e) =>
                                handleInputChange(
                                  "gstin",
                                  e.target.value.toUpperCase(),
                                )
                              }
                            />
                            <FormErrorMessage>{errors.gstin}</FormErrorMessage>
                          </FormControl>

                          <FormControl>
                            <FormLabel>HSN / SAC</FormLabel>
                            <Input
                              placeholder="Enter HSN/SAC code"
                              value={formData.hsnSacCode}
                              isDisabled={isReadOnlyProposal}
                              onChange={(e) =>
                                handleInputChange("hsnSacCode", e.target.value)
                              }
                            />
                          </FormControl>

                          <FormControl>
                            <FormLabel>Place of Supply</FormLabel>
                            <Input
                              placeholder="MAHARASHTRA"
                              value={formData.placeOfSupply}
                              isDisabled={isReadOnlyProposal}
                              onChange={(e) =>
                                handleInputChange(
                                  "placeOfSupply",
                                  e.target.value.toUpperCase(),
                                )
                              }
                            />
                          </FormControl>

                          <FormControl>
                            <FormLabel>Base Currency</FormLabel>
                            <Select
                              value={formData.baseCurrency}
                              isDisabled={isReadOnlyProposal}
                              onChange={(e) =>
                                handleInputChange(
                                  "baseCurrency",
                                  e.target.value,
                                )
                              }
                            >
                              <option value="INR">INR</option>
                              <option value="USD">USD</option>
                              <option value="EUR">EUR</option>
                              <option value="GBP">GBP</option>
                            </Select>
                          </FormControl>

                          <FormControl>
                            <FormLabel>Exchange Rate</FormLabel>
                            <Input
                              type="text"
                              inputMode="decimal"
                              value={formData.exchangeRate}
                              isDisabled={isReadOnlyProposal}
                              onChange={(e) =>
                                handleTextNumberChange(
                                  "exchangeRate",
                                  e.target.value,
                                )
                              }
                            />
                          </FormControl>

                          <FormControl>
                            <FormLabel>Tax Type</FormLabel>
                            <Select
                              value={formData.taxType}
                              isDisabled={isReadOnlyProposal}
                              onChange={(e) =>
                                handleInputChange("taxType", e.target.value)
                              }
                            >
                              <option value="GST">GST</option>
                              <option value="IGST">IGST</option>
                              <option value="TAX">TAX</option>
                            </Select>
                          </FormControl>

                          <FormControl
                            display="flex"
                            alignItems="center"
                            pt={6}
                          >
                            <Checkbox
                              isChecked={Boolean(
                                formData.reverseChargeApplicable,
                              )}
                              isDisabled={isReadOnlyProposal}
                              onChange={(e) =>
                                handleInputChange(
                                  "reverseChargeApplicable",
                                  e.target.checked,
                                )
                              }
                            >
                              Reverse charge applicable
                            </Checkbox>
                          </FormControl>

                          <FormControl>
                            <FormLabel>Quotation Valid Till</FormLabel>
                            <Input
                              type="date"
                              value={formData.quotationValidUntil}
                              isDisabled={isReadOnlyProposal}
                              onChange={(e) =>
                                handleInputChange(
                                  "quotationValidUntil",
                                  e.target.value,
                                )
                              }
                            />
                          </FormControl>

                          <FormControl>
                            <FormLabel>Expected Delivery Date</FormLabel>
                            <Input
                              type="date"
                              value={formData.expectedDeliveryDate}
                              isDisabled={isReadOnlyProposal}
                              onChange={(e) =>
                                handleInputChange(
                                  "expectedDeliveryDate",
                                  e.target.value,
                                )
                              }
                            />
                          </FormControl>

                          <FormControl>
                            <FormLabel>Lead Time (days)</FormLabel>
                            <Input
                              type="text"
                              inputMode="numeric"
                              value={formData.leadTime}
                              isDisabled={isReadOnlyProposal}
                              onChange={(e) =>
                                handleTextNumberChange(
                                  "leadTime",
                                  e.target.value,
                                )
                              }
                            />
                          </FormControl>
                        </SimpleGrid>
                        {errors.tax ? (
                          <Text mt={3} color="red.500" fontSize="sm">
                            {errors.tax}
                          </Text>
                        ) : null}
                      </CardBody>
                    </Card>
                  </SimpleGrid>

                  <Card
                    variant="outline"
                    borderRadius="xl"
                    boxShadow="sm"
                    mt={6}
                    transform="scale(0.8)"
                    transformOrigin="top left"
                    width="125%"
                  >
                    <CardBody>
                      <HStack justify="space-between" align="flex-start" mb={4}>
                        <Box>
                          <Text fontWeight="semibold" fontSize="lg">
                            Quotation Line Items
                          </Text>
                          <Text fontSize="sm" color="gray.500">
                            Header GST automatically propagates to every line.
                          </Text>
                        </Box>
                        <Button
                          leftIcon={<BsPlus />}
                          size="sm"
                          variant="outline"
                          onClick={addLineItem}
                          isDisabled={isReadOnlyProposal}
                        >
                          Add Row
                        </Button>
                      </HStack>

                      <SimpleGrid
                        columns={{ base: 2, md: 4 }}
                        spacing={3}
                        mb={4}
                      >
                        {[
                          ["Total Items", proposalLineItems.length],
                          [
                            "Total Quantity",
                            proposalLineItems.reduce(
                              (sum, item) => sum + Number(item.quantity || 0),
                              0,
                            ),
                          ],
                          ["Total Tax", formData.totalTaxAmount],
                          ["Grand Total", formData.grandTotal],
                        ].map(([label, value]) => (
                          <Box
                            key={label}
                            p={3}
                            borderWidth="1px"
                            borderRadius="lg"
                          >
                            <Text fontSize="xs" color="gray.500">
                              {label}
                            </Text>
                            <Text fontSize="lg" fontWeight="semibold">
                              {label === "Grand Total" || label === "Total Tax"
                                ? formatCurrency(value)
                                : value}
                            </Text>
                          </Box>
                        ))}
                      </SimpleGrid>

                      <Box display={{ base: "none", lg: "block" }}>
                        <Box
                          borderWidth="1px"
                          borderRadius="xl"
                          overflowX="auto"
                        >
                          <Table size="md" variant="">
                            <Thead
                              position="sticky"
                              top={0}
                              zIndex={1}
                              bg="white"
                            >
                              <Tr>
                                <Th width="18%">Item</Th>
                                <Th width="24%">Description</Th>
                                <Th isNumeric width="8%">
                                  Qty
                                </Th>
                                <Th width="8%">Unit</Th>
                                <Th isNumeric width="12%">
                                  Unit Price
                                </Th>
                                <Th isNumeric width="10%">
                                  Discount
                                </Th>
                                <Th isNumeric width="8%">
                                  GST %
                                </Th>
                                <Th isNumeric width="10%">
                                  <Tooltip
                                    label="(Total - Discount) × GST %"
                                    hasArrow
                                  >
                                    <span>Tax Amount</span>
                                  </Tooltip>
                                </Th>
                                <Th isNumeric width="10%">
                                  Net Amount
                                </Th>
                                <Th></Th>
                              </Tr>
                            </Thead>
                            <Tbody>
                              {proposalLineItems.map((item) => {
                                const invalid = lineItemHasError(item);
                                return (
                                  <Tr
                                    key={item.id}
                                    bg={invalid ? "red.50" : undefined}
                                  >
                                    <Td>
                                      <Input
                                        size="md"
                                        value={item.itemName}
                                        isReadOnly={
                                          Boolean(item.requestLineItemId) ||
                                          isReadOnlyProposal
                                        }
                                        onChange={(e) =>
                                          updateLineItem(
                                            item.id,
                                            "itemName",
                                            e.target.value,
                                          )
                                        }
                                        placeholder="Item name"
                                      />
                                    </Td>
                                    <Td>
                                      <Input
                                        size="md"
                                        value={item.description}
                                        isReadOnly={isReadOnlyProposal}
                                        onChange={(e) =>
                                          updateLineItem(
                                            item.id,
                                            "description",
                                            e.target.value,
                                          )
                                        }
                                        placeholder="Description"
                                      />
                                    </Td>
                                    <Td isNumeric>
                                      <Input
                                        size="md"
                                        type="text"
                                        inputMode="numeric"
                                        value={item.quantity}
                                        isReadOnly={isReadOnlyProposal}
                                        onChange={(e) =>
                                          updateLineItem(
                                            item.id,
                                            "quantity",
                                            sanitizeNumericInput(
                                              e.target.value,
                                            ),
                                          )
                                        }
                                      />
                                    </Td>
                                    <Td>
                                      <Input
                                        size="md"
                                        value={item.unit}
                                        isReadOnly={isReadOnlyProposal}
                                        onChange={(e) =>
                                          updateLineItem(
                                            item.id,
                                            "unit",
                                            e.target.value,
                                          )
                                        }
                                        placeholder="pcs"
                                      />
                                    </Td>
                                    <Td isNumeric>
                                      <Input
                                        size="md"
                                        type="text"
                                        inputMode="decimal"
                                        value={item.unitPrice}
                                        isReadOnly={isReadOnlyProposal}
                                        onChange={(e) =>
                                          updateLineItem(
                                            item.id,
                                            "unitPrice",
                                            sanitizeNumericInput(
                                              e.target.value,
                                            ),
                                          )
                                        }
                                        placeholder="0"
                                      />
                                    </Td>
                                    <Td isNumeric>
                                      <Input
                                        size="md"
                                        type="text"
                                        inputMode="decimal"
                                        value={item.discountAmount}
                                        isReadOnly={isReadOnlyProposal}
                                        onChange={(e) =>
                                          updateLineItem(
                                            item.id,
                                            "discountAmount",
                                            sanitizeNumericInput(
                                              e.target.value,
                                            ),
                                          )
                                        }
                                        placeholder="0"
                                      />
                                    </Td>
                                    <Td isNumeric>
                                      <Input
                                        size="md"
                                        value={item.taxRate}
                                        isReadOnly
                                        textAlign="center"
                                      />
                                    </Td>
                                    <Td isNumeric fontWeight="semibold">
                                      {formatCurrency(
                                        normalizeAmount(item.taxAmount),
                                      )}
                                    </Td>
                                    <Td isNumeric fontWeight="bold">
                                      {formatCurrency(
                                        normalizeAmount(item.netAmount),
                                      )}
                                    </Td>
                                    <Td>
                                      <IconButton
                                        icon={<BsTrash />}
                                        size="sm"
                                        variant="ghost"
                                        colorScheme="red"
                                        onClick={() => removeLineItem(item.id)}
                                        isDisabled={
                                          proposalLineItems.length === 1 ||
                                          isReadOnlyProposal
                                        }
                                        aria-label="Remove quotation line item"
                                      />
                                    </Td>
                                  </Tr>
                                );
                              })}
                            </Tbody>
                          </Table>
                        </Box>
                      </Box>

                      <Box display={{ base: "block", lg: "none" }}>
                        <VStack spacing={4} align="stretch">
                          {proposalLineItems.map((item, index) => {
                            const invalid = lineItemHasError(item);
                            return (
                              <Card
                                key={item.id}
                                borderWidth="1px"
                                borderColor={invalid ? "red.300" : "gray.200"}
                                bg={invalid ? "red.50" : "white"}
                              >
                                <CardBody>
                                  <HStack justify="space-between" mb={3}>
                                    <Text fontWeight="semibold">
                                      Line Item {index + 1}
                                    </Text>
                                    <IconButton
                                      icon={<BsTrash />}
                                      size="sm"
                                      variant="ghost"
                                      colorScheme="red"
                                      onClick={() => removeLineItem(item.id)}
                                      isDisabled={
                                        proposalLineItems.length === 1 ||
                                        isReadOnlyProposal
                                      }
                                      aria-label="Remove quotation line item"
                                    />
                                  </HStack>
                                  <SimpleGrid
                                    columns={{ base: 1, md: 2 }}
                                    spacing={3}
                                  >
                                    <FormControl>
                                      <FormLabel>Item</FormLabel>
                                      <Input
                                        value={item.itemName}
                                        isReadOnly={
                                          Boolean(item.requestLineItemId) ||
                                          isReadOnlyProposal
                                        }
                                        onChange={(e) =>
                                          updateLineItem(
                                            item.id,
                                            "itemName",
                                            e.target.value,
                                          )
                                        }
                                      />
                                    </FormControl>
                                    <FormControl>
                                      <FormLabel>Description</FormLabel>
                                      <Input
                                        value={item.description}
                                        isReadOnly={isReadOnlyProposal}
                                        onChange={(e) =>
                                          updateLineItem(
                                            item.id,
                                            "description",
                                            e.target.value,
                                          )
                                        }
                                      />
                                    </FormControl>
                                    <FormControl>
                                      <FormLabel>Qty</FormLabel>
                                      <Input
                                        type="text"
                                        inputMode="numeric"
                                        value={item.quantity}
                                        isReadOnly={isReadOnlyProposal}
                                        onChange={(e) =>
                                          updateLineItem(
                                            item.id,
                                            "quantity",
                                            sanitizeNumericInput(
                                              e.target.value,
                                            ),
                                          )
                                        }
                                      />
                                    </FormControl>
                                    <FormControl>
                                      <FormLabel>Unit</FormLabel>
                                      <Input
                                        value={item.unit}
                                        isReadOnly={isReadOnlyProposal}
                                        onChange={(e) =>
                                          updateLineItem(
                                            item.id,
                                            "unit",
                                            e.target.value,
                                          )
                                        }
                                      />
                                    </FormControl>
                                    <FormControl>
                                      <FormLabel>Unit Price</FormLabel>
                                      <Input
                                        type="text"
                                        inputMode="decimal"
                                        value={item.unitPrice}
                                        isReadOnly={isReadOnlyProposal}
                                        onChange={(e) =>
                                          updateLineItem(
                                            item.id,
                                            "unitPrice",
                                            sanitizeNumericInput(
                                              e.target.value,
                                            ),
                                          )
                                        }
                                      />
                                    </FormControl>
                                    <FormControl>
                                      <FormLabel>Discount</FormLabel>
                                      <Input
                                        type="text"
                                        inputMode="decimal"
                                        value={item.discountAmount}
                                        isReadOnly={isReadOnlyProposal}
                                        onChange={(e) =>
                                          updateLineItem(
                                            item.id,
                                            "discountAmount",
                                            sanitizeNumericInput(
                                              e.target.value,
                                            ),
                                          )
                                        }
                                      />
                                    </FormControl>
                                  </SimpleGrid>
                                  <SimpleGrid
                                    columns={{ base: 1, md: 3 }}
                                    spacing={3}
                                    mt={3}
                                  >
                                    <Box>
                                      <Text fontSize="xs" color="gray.500">
                                        GST %
                                      </Text>
                                      <Text fontWeight="semibold">
                                        {item.taxRate || 0}
                                      </Text>
                                    </Box>
                                    <Box>
                                      <Text fontSize="xs" color="gray.500">
                                        Tax Amount
                                      </Text>
                                      <Text fontWeight="semibold">
                                        {formatCurrency(
                                          normalizeAmount(item.taxAmount),
                                        )}
                                      </Text>
                                    </Box>
                                    <Box>
                                      <Text fontSize="xs" color="gray.500">
                                        Net Amount
                                      </Text>
                                      <Text fontWeight="bold">
                                        {formatCurrency(
                                          normalizeAmount(item.netAmount),
                                        )}
                                      </Text>
                                    </Box>
                                  </SimpleGrid>
                                </CardBody>
                              </Card>
                            );
                          })}
                        </VStack>
                      </Box>

                      <HStack
                        justify="space-between"
                        mt={4}
                        align="flex-start"
                        flexWrap="wrap"
                      >
                        <Text fontSize="xs" color="gray.500">
                          GST is driven by the header configuration and pushed
                          to every line.
                        </Text>
                        <Text fontWeight="semibold">
                          Total: {formatCurrency(calculateQuotedAmount())}
                        </Text>
                      </HStack>
                      <FormErrorMessage>{errors.lineItems}</FormErrorMessage>
                    </CardBody>
                  </Card>

                  <SimpleGrid
                    columns={{ base: 1, xl: 2 }}
                    spacing={6}
                    mt={3}
                    alignItems="start"
                  >
                    {/* LEFT SIDE — FINANCIAL SUMMARY */}

                    {/* RIGHT SIDE — TERMS */}

                    <Card
                      variant="outline"
                      borderRadius="xl"
                      boxShadow="sm"
                      h="100%"
                    >
                      <CardBody>
                        <Box mb={4}>
                          <Text fontWeight="semibold" fontSize="lg">
                            Terms & Conditions
                          </Text>

                          <Text fontSize="sm" color="gray.500">
                            Commercial terms, payment, delivery, and warranty.
                          </Text>
                        </Box>

                        <SimpleGrid
                          columns={{
                            base: 1,
                            md: 2,
                          }}
                          spacing={4}
                        >
                          <FormControl>
                            <FormLabel>Terms & Conditions</FormLabel>

                            <Textarea
                              value={formData.termsAndConditions}
                              isReadOnly={isReadOnlyProposal}
                              onChange={(e) =>
                                handleInputChange(
                                  "termsAndConditions",
                                  e.target.value,
                                )
                              }
                              rows={4}
                              placeholder="Enter commercial terms and general conditions"
                            />
                          </FormControl>

                          <FormControl>
                            <FormLabel>Payment Terms</FormLabel>

                            <Textarea
                              value={formData.paymentTerms}
                              isReadOnly={isReadOnlyProposal}
                              onChange={(e) =>
                                handleInputChange(
                                  "paymentTerms",
                                  e.target.value,
                                )
                              }
                              rows={4}
                              placeholder="e.g. Net 30 days, 50% advance"
                            />
                          </FormControl>

                          <FormControl>
                            <FormLabel>Delivery Timeline</FormLabel>

                            <Textarea
                              value={formData.deliveryTimeline}
                              isReadOnly={isReadOnlyProposal}
                              onChange={(e) =>
                                handleInputChange(
                                  "deliveryTimeline",
                                  e.target.value,
                                )
                              }
                              rows={3}
                              placeholder="Expected delivery schedule"
                            />
                          </FormControl>

                          <FormControl>
                            <FormLabel>Warranty Terms</FormLabel>

                            <Textarea
                              value={formData.warrantyTerms}
                              isReadOnly={isReadOnlyProposal}
                              onChange={(e) =>
                                handleInputChange(
                                  "warrantyTerms",
                                  e.target.value,
                                )
                              }
                              rows={3}
                              placeholder="Warranty and support coverage"
                            />
                          </FormControl>

                          <FormControl>
                            <FormLabel>Special Notes</FormLabel>

                            <Textarea
                              value={formData.specialNotes}
                              isReadOnly={isReadOnlyProposal}
                              onChange={(e) =>
                                handleInputChange(
                                  "specialNotes",
                                  e.target.value,
                                )
                              }
                              rows={3}
                              placeholder="Any special instructions or notes"
                            />
                          </FormControl>
                        </SimpleGrid>
                      </CardBody>
                    </Card>

                    <Card
                      variant="outline"
                      borderRadius="xl"
                      boxShadow="lg"
                      borderColor="blue.100"
                      h="100%"
                    >
                      <CardBody>
                        <HStack justify="space-between" mb={2} flexWrap="wrap">
                          <Text fontWeight="semibold" fontSize="lg">
                            Financial Summary
                          </Text>

                          <HStack>
                            <Badge
                              colorScheme={
                                effectiveTaxRate > 0 ? "green" : "gray"
                              }
                            >
                              GST Applied
                            </Badge>

                            <Badge
                              colorScheme={
                                proposalStatus === "REJECTED"
                                  ? "red"
                                  : isReadOnlyProposal
                                    ? "purple"
                                    : "blue"
                              }
                            >
                              {proposalStatus === "REJECTED"
                                ? "Rejected"
                                : isReadOnlyProposal
                                  ? "Approved"
                                  : "Draft"}
                            </Badge>
                          </HStack>
                        </HStack>

                        <Text fontSize="sm" color="gray.500" mb={4}>
                          Live totals update as commercial values change.
                        </Text>

                        <VStack spacing={3} align="stretch">
                          {[
                            ["Subtotal", formData.subtotal],
                            ["Header Discount", formData.discountAmount],
                            ["Shipping Charges", formData.shippingCharges],
                            ["Packing Charges", formData.packingCharges],
                            ["Other Charges", formData.otherCharges],
                          ].map(([label, value]) => (
                            <HStack key={label} justify="space-between">
                              <Text fontSize="sm" color="gray.600">
                                {label}
                              </Text>

                              <Text fontWeight="medium">
                                {formatCurrency(value)}
                              </Text>
                            </HStack>
                          ))}

                          <Divider />

                          <HStack justify="space-between">
                            <Text fontSize="sm" color="gray.600">
                              Taxable Amount
                            </Text>

                            <Text fontWeight="semibold">
                              {formatCurrency(formData.taxableAmount)}
                            </Text>
                          </HStack>

                          <HStack justify="space-between">
                            <Text fontSize="sm" color="gray.600">
                              CGST
                            </Text>

                            <Text>{formatCurrency(formData.cgstAmount)}</Text>
                          </HStack>

                          <HStack justify="space-between">
                            <Text fontSize="sm" color="gray.600">
                              SGST
                            </Text>

                            <Text>{formatCurrency(formData.sgstAmount)}</Text>
                          </HStack>

                          <HStack justify="space-between">
                            <Text fontSize="sm" color="gray.600">
                              IGST
                            </Text>

                            <Text>{formatCurrency(formData.igstAmount)}</Text>
                          </HStack>

                          <HStack justify="space-between">
                            <Text fontSize="sm" color="gray.600">
                              Total Tax
                            </Text>

                            <Text>
                              {formatCurrency(formData.totalTaxAmount)}
                            </Text>
                          </HStack>

                          <HStack justify="space-between">
                            <Text fontSize="sm" color="gray.600">
                              Round Off
                            </Text>

                            <Text>
                              {formatCurrency(formData.roundOffAmount)}
                            </Text>
                          </HStack>

                          <Divider />

                          <Box
                            p={5}
                            borderRadius="xl"
                            bg="blue.50"
                            borderWidth="1px"
                            borderColor="blue.100"
                          >
                            <HStack justify="space-between" align="flex-end">
                              <Text fontWeight="semibold" fontSize="md">
                                Grand Total
                              </Text>

                              <Text
                                fontWeight="bold"
                                fontSize="3xl"
                                color="blue.700"
                                lineHeight="1"
                              >
                                {formatCurrency(formData.grandTotal)}
                              </Text>
                            </HStack>
                          </Box>
                        </VStack>
                      </CardBody>
                    </Card>
                  </SimpleGrid>

                  <Card
                    variant="outline"
                    borderRadius="xl"
                    boxShadow="sm"
                    mt={6}
                  >
                    <CardBody>
                      <Text fontWeight="semibold" fontSize="lg" mb={4}>
                        Document Upload
                      </Text>
                      <Input
                        ref={poDocumentInputRef}
                        type="file"
                        display="none"
                        onChange={(e) =>
                          setPoDocumentFile(e.target.files?.[0] || null)
                        }
                      />
                      <Box
                        borderWidth="1px"
                        borderStyle="dashed"
                        borderRadius="xl"
                        p={5}
                        bg="gray.50"
                        cursor={isReadOnlyProposal ? "default" : "pointer"}
                        onClick={() =>
                          !isReadOnlyProposal &&
                          poDocumentInputRef.current?.click()
                        }
                      >
                        {poDocumentFile ? (
                          <Stack spacing={2}>
                            <HStack justify="space-between" flexWrap="wrap">
                              <Box>
                                <Text fontWeight="semibold">
                                  {poDocumentFile.name}
                                </Text>
                                <Text fontSize="sm" color="gray.500">
                                  {formatFileSize(poDocumentFile.size)}
                                </Text>
                              </Box>
                              <Badge colorScheme="green">Ready to upload</Badge>
                            </HStack>
                            <HStack spacing={2}>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  poDocumentInputRef.current?.click();
                                }}
                                isDisabled={isReadOnlyProposal}
                              >
                                Replace File
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                colorScheme="red"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setPoDocumentFile(null);
                                  if (poDocumentInputRef.current) {
                                    poDocumentInputRef.current.value = "";
                                  }
                                }}
                                isDisabled={isReadOnlyProposal}
                              >
                                Remove
                              </Button>
                            </HStack>
                          </Stack>
                        ) : (
                          <Stack spacing={2} textAlign="center">
                            <Text fontWeight="semibold">
                              Drag & drop quotation PDF or browse files
                            </Text>
                            <Text fontSize="sm" color="gray.500">
                              PDF, DOC, or image attachments can be uploaded as
                              quotation proof.
                            </Text>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={(e) => {
                                e.stopPropagation();
                                poDocumentInputRef.current?.click();
                              }}
                              isDisabled={isReadOnlyProposal}
                            >
                              Browse Files
                            </Button>
                          </Stack>
                        )}
                      </Box>
                      {uploadingDocument ? (
                        <Text mt={3} fontSize="sm" color="blue.600">
                          Upload in progress...
                        </Text>
                      ) : null}
                    </CardBody>
                  </Card>
                </Box>

                {/* <Box position="sticky" top="4" alignSelf="start">
                  <Card
                    variant="outline"
                    borderRadius="xl"
                    boxShadow="lg"
                    borderColor="blue.100"
                  >
                    <CardBody>
                      <HStack justify="space-between" mb={2} flexWrap="wrap">
                        <Text fontWeight="semibold" fontSize="lg">
                          Financial Summary
                        </Text>
                        <HStack>
                          <Badge
                            colorScheme={
                              effectiveTaxRate > 0 ? "green" : "gray"
                            }
                          >
                            GST Applied
                          </Badge>
                          <Badge
                            colorScheme={
                              proposalStatus === "REJECTED"
                                ? "red"
                                : isReadOnlyProposal
                                  ? "purple"
                                  : "blue"
                            }
                          >
                            {proposalStatus === "REJECTED"
                              ? "Rejected"
                              : isReadOnlyProposal
                                ? "Approved"
                                : "Draft"}
                          </Badge>
                        </HStack>
                      </HStack>
                      <Text fontSize="sm" color="gray.500" mb={4}>
                        Live totals update as commercial values change.
                      </Text>
                      <VStack spacing={3} align="stretch">
                        {[
                          ["Subtotal", formData.subtotal],
                          ["Header Discount", formData.discountAmount],
                          ["Shipping Charges", formData.shippingCharges],
                          ["Packing Charges", formData.packingCharges],
                          ["Other Charges", formData.otherCharges],
                        ].map(([label, value]) => (
                          <HStack key={label} justify="space-between">
                            <Text fontSize="sm" color="gray.600">
                              {label}
                            </Text>
                            <Text fontWeight="medium">
                              {formatCurrency(value)}
                            </Text>
                          </HStack>
                        ))}

                        <Divider />

                        <HStack justify="space-between">
                          <Text fontSize="sm" color="gray.600">
                            Taxable Amount
                          </Text>
                          <Text fontWeight="semibold">
                            {formatCurrency(formData.taxableAmount)}
                          </Text>
                        </HStack>
                        <HStack justify="space-between">
                          <Text fontSize="sm" color="gray.600">
                            CGST
                          </Text>
                          <Text>{formatCurrency(formData.cgstAmount)}</Text>
                        </HStack>
                        <HStack justify="space-between">
                          <Text fontSize="sm" color="gray.600">
                            SGST
                          </Text>
                          <Text>{formatCurrency(formData.sgstAmount)}</Text>
                        </HStack>
                        <HStack justify="space-between">
                          <Text fontSize="sm" color="gray.600">
                            IGST
                          </Text>
                          <Text>{formatCurrency(formData.igstAmount)}</Text>
                        </HStack>
                        <HStack justify="space-between">
                          <Text fontSize="sm" color="gray.600">
                            Total Tax
                          </Text>
                          <Text>{formatCurrency(formData.totalTaxAmount)}</Text>
                        </HStack>
                        <HStack justify="space-between">
                          <Text fontSize="sm" color="gray.600">
                            Round Off
                          </Text>
                          <Text>{formatCurrency(formData.roundOffAmount)}</Text>
                        </HStack>

                        <Divider />

                        <Box
                          p={4}
                          borderRadius="lg"
                          bg="blue.50"
                          borderWidth="1px"
                          borderColor="blue.100"
                        >
                          <HStack justify="space-between" align="flex-end">
                            <Text fontWeight="semibold" fontSize="md">
                              Grand Total
                            </Text>
                            <Text
                              fontWeight="bold"
                              fontSize="2xl"
                              color="blue.700"
                            >
                              {formatCurrency(formData.grandTotal)}
                            </Text>
                          </HStack>
                        </Box>
                      </VStack>
                    </CardBody>
                  </Card>
                </Box> */}
              </SimpleGrid>
            </VStack>
          </ShellBody>

          <ShellFooter
            px={isPageMode ? { base: 4, md: 6 } : undefined}
            pb={isPageMode ? { base: 5, md: 6 } : undefined}
            display={isPageMode ? "flex" : undefined}
            justifyContent={isPageMode ? "flex-end" : undefined}
            alignItems={isPageMode ? "center" : undefined}
            flexWrap={isPageMode ? "wrap" : undefined}
            gap={isPageMode ? 3 : undefined}
          >
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              type="submit"
              isLoading={loading || uploadingDocument}
              isDisabled={
                vendors.length === 0 || !requestId || isReadOnlyProposal
              }
            >
              {isReadOnlyProposal ? "Approved" : "Add Proposal"}
            </Button>
          </ShellFooter>
        </form>
      </ShellContent>
    </Shell>
  );
};

export default AddVendorPOModal;
